import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, X } from 'lucide-react'
import { subscribeForegroundMessages } from '@/lib/pushNotifications'

interface FcmMessage {
  title: string
  body?: string
  url?: string
}

export function NotificationBanner() {
  const [message, setMessage] = useState<FcmMessage | null>(null)
  const navigate = useNavigate()
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Mount the foreground FCM listener at app level
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

    let unsubscribe: (() => void) | undefined
    subscribeForegroundMessages().then((unsub) => {
      unsubscribe = unsub
    })
    return () => unsubscribe?.()
  }, [])

  // Listen for custom events dispatched by the foreground listener
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as FcmMessage
      clearTimeout(timerRef.current)
      setMessage(detail)
      timerRef.current = setTimeout(() => setMessage(null), 6000)
    }
    window.addEventListener('fcm-foreground', handler)
    return () => {
      window.removeEventListener('fcm-foreground', handler)
      clearTimeout(timerRef.current)
    }
  }, [])

  const handleClick = useCallback(() => {
    if (message?.url) {
      if (message.url.startsWith('/')) {
        navigate(message.url)
      } else {
        try {
          const url = new URL(message.url)
          navigate(url.pathname)
        } catch {
          navigate(message.url)
        }
      }
    }
    setMessage(null)
  }, [message, navigate])

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50 max-w-sm w-full"
        >
          <div
            onClick={handleClick}
            className="flex items-start gap-3 rounded-lg border bg-card p-4 shadow-lg cursor-pointer hover:bg-accent/50 transition-colors"
          >
            <Bell className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{message.title}</p>
              {message.body && (
                <p className="text-sm text-muted-foreground mt-0.5 truncate">{message.body}</p>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setMessage(null)
              }}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
